import {Box, Stack, Typography} from '@mui/material';
import {DataGrid, GridColDef, GridRowSelectionModel} from '@mui/x-data-grid';
import {useMemo, useState, useEffect} from 'react';

// import {AccessGroup} from 'src/apis/autoGenerate';
import {AccessGroup, Tenant} from 'src/apis/autoGenerate/types';
import Modal from 'src/components/Modal';
import SearchInput from 'src/components/SearchInput';
import {useTenants} from 'src/hooks';
import {formatDate} from 'src/utils/common';

interface AddTenantToAccessGroupModalProps {
  open: boolean;
  handleConfirm: (tenantIds: string[]) => void;
  handleCancel: VoidFunction;
  targetGroup: AccessGroup;
  existingTenants?: Tenant[];
}

export default function AddTenantToAccessGroupModal({
  open,
  handleConfirm,
  handleCancel,
  targetGroup,
  existingTenants = [],
}: AddTenantToAccessGroupModalProps) {
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const {loadingTenants, tenants, getTenants} = useTenants();
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    if (open) {
      getTenants();
    }
  }, [open, getTenants]);

  // Filter out tenants that are already in the access group
  useEffect(() => {
    if (tenants.length) {
      if (existingTenants && existingTenants.length) {
        // Create a set of existing tenant IDs for faster lookup
        const existingTenantIds = new Set(existingTenants.map(tenant => tenant.id));

        // Filter the tenants list to exclude those already in the group
        const filtered = tenants.filter(tenant => !existingTenantIds.has(tenant.id));
        setFilteredTenants(filtered);
      } else {
        setFilteredTenants(tenants);
      }
    } else {
      setFilteredTenants([]);
    }
  }, [tenants, existingTenants]);

  const handleAddTenants = () => {
    if (selectedRowIds.length > 0) {
      handleConfirm(selectedRowIds as string[]);
    }
  };

  const handleSearchTenantByInfo = (name: string) => {
    getTenants(name);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'user',
        headerName: 'Name',
        width: 220,
        renderCell: val => {
          return (
            <Stack>
              <Typography variant="body2" sx={{fontSize: 14, fontWeight: 500}}>{`${
                val.value?.firstName || ''
              } ${val.value?.lastName || ''}`}</Typography>
              <Typography variant="body2" sx={{color: theme => theme.palette.text.secondary}}>
                {val.value?.email || ''}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'room',
        headerName: 'Unit Name',
        width: 130,
        renderCell: val => {
          return <Box>{val.value?.name || ''}</Box>;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Create Date',
        width: 150,
        renderCell: val => {
          return <Box>{formatDate(val.value)}</Box>;
        },
      },
    ];
  }, []);

  return (
    <Modal
      open={open}
      title={`Assign Residents to ${targetGroup.name}`}
      handleCancel={handleCancel}
      handleConfirm={handleAddTenants}
      modalContent={
        <Box position={'relative'} height={'70vh'}>
          <Box sx={{position: 'fixed', zIndex: 2, backgroundColor: '#fff'}}>
            <SearchInput placeholder="Search Resident" onSearch={handleSearchTenantByInfo} />
          </Box>
          <Box
            sx={{position: 'absolute', height: 'calc(70vh - 50px)', overflow: 'scroll', top: 50}}
          >
            <DataGrid
              loading={loadingTenants}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rows={filteredTenants.length ? filteredTenants : []}
              columns={columns}
              checkboxSelection
              initialState={{
                pagination: {
                  paginationModel: {page: 0, pageSize: 10},
                },
              }}
              onRowSelectionModelChange={res => {
                setSelectedRowIds(res);
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box textAlign="center" my={2}>
                    No Residents
                  </Box>
                ),
              }}
              pageSizeOptions={[5, 10]}
            />
          </Box>
        </Box>
      }
    />
  );
}
